// Pure arithmetic behind the countdown — CCT-288.
//
// ⚠️ SPLIT FROM THE HTTP CLIENT SO IT CAN BE TESTED. As long as these functions sat next to an
// `import { apiFetch }`, loading them in a test dragged in the whole network stack and the Node
// harness could not do it — so the only genuinely checkable part of the feature was not being
// checked. This file imports NOTHING.
//
// ⚠️ AND THIS IS ONLY A DISPLAY. The verdict belongs to the server, which re-checks the
// deadline on submission. A mistake here shows a wrong number; it does not grant time.
export interface ChronoVue {
  elementId: string | null;
  scope: 'mission' | 'element';
  limitSeconds: number;
  onTimeout: 'fail_task' | 'fail_mission';
  outcome: 'expired' | 'done' | null;
  /** ISO 8601 instant WITH offset — the only value worth counting from. */
  deadlineAt: string | null;
  remainingSeconds: number;
}

/**
 * Seconds left on a timer, from its ABSOLUTE deadline.
 *
 * ⚠️ CLAMPED AT ZERO, NEVER NEGATIVE. A clock skew between the browser and the server would
 * otherwise render "-14 s", which reads as a bug rather than as "time is up".
 */
export function secondesRestantes(t: ChronoVue | null | undefined, maintenant = Date.now()): number {
  if (!t || !t.deadlineAt) return 0;
  const fin = Date.parse(t.deadlineAt);
  if (Number.isNaN(fin)) return 0;
  return Math.max(0, Math.round((fin - maintenant) / 1000));
}

/** `mm:ss`, or `h:mm:ss` past an hour. */
export function formatChrono(secondes: number): string {
  const s = Math.max(0, Math.floor(secondes));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const deux = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${deux(m)}:${deux(r)}` : `${deux(m)}:${deux(r)}`;
}

/**
 * The one timer the header should show.
 *
 * ⚠️ THE MOST URGENT WINS, AND A MISSION-WIDE ONE ONLY WHEN IT IS. A player inside a timed quiz
 * needs the quiz clock; showing the mission's two-hour budget instead would be true and useless.
 * Expired timers are skipped here — the header counts down, it does not announce verdicts.
 */
export function chronoAAfficher(
  timers: ChronoVue[], elementOuvert: string | null, maintenant = Date.now(),
): ChronoVue | null {
  const vivants = timers.filter(t => !t.outcome && t.deadlineAt);
  if (!vivants.length) return null;
  const courant = elementOuvert
    ? vivants.find(t => t.elementId === elementOuvert)
    : undefined;
  if (courant) return courant;
  return vivants.reduce((a, b) =>
    secondesRestantes(a, maintenant) <= secondesRestantes(b, maintenant) ? a : b);
}

/**
 * Does this response mean "you ran out of time"?
 *
 * ⚠️ ON THE CODE, NOT ON THE MESSAGE. Server messages are translated and get rewritten; the
 * error code does not. Matching on wording is how a refusal quietly stops being recognised.
 */
export function estDepassementDeDelai(err: any): boolean {
  const d = err?.detail ?? err?.body?.detail ?? err;
  return d?.error_code === 'TIME_LIMIT_EXCEEDED' || err?.status === 409
    ? d?.error_code === 'TIME_LIMIT_EXCEEDED'
    : false;
}
